import type { AllMembershipPlanType } from '@frontend/api-client';
import { getMembershipPlanDisplayPricing, isHousehold } from '@frontend/api-client';
import type { Household, PromotionDetails } from '@frontend/api-types';
import type { StrictExtract } from '@frontend/duck-tape';
import { formatCurrencyNatural, match, pluralize } from '@frontend/duck-tape';
import type { TextColorVariant } from '@frontend/web-react';
import { Button, Card, Icon, List, Text, XStack, YStack, getTextColorClassName } from '@frontend/web-react';
import type { IconName } from '@frontend/web-react/components/Primitives/Icon/helpers';
import type { ClassName } from '@frontend/web-utils';
import { applyClass, concatClasses, safeCss } from '@frontend/web-utils';
import { useMembershipPlans } from '@frontend/web/hooks';

export type MembershipPlanCardProps = {
  className?: ClassName;
  currentClientId?: string;
  hideBenefitDetails?: boolean;
  hidePrice?: boolean;
  household?: Household;
  isSelected: boolean;
  onClick?: (plan: AllMembershipPlanType) => void;
  plan: AllMembershipPlanType;
  promoDetails: MaybeUndefined<PromotionDetails>;
  showCurrentUsers?: boolean;
  showOwner?: boolean;
};

export const MembershipPlanCard = ({
  className,
  currentClientId,
  hideBenefitDetails = false,
  hidePrice,
  household,
  isSelected,
  onClick,
  plan,
  promoDetails,
  showCurrentUsers,
  showOwner,
}: MembershipPlanCardProps) => {
  const { isError, isLoading, planLookup, refetch } = useMembershipPlans();

  const { color, name } = match<
    AllMembershipPlanType,
    { color: StrictExtract<TextColorVariant, 'pink' | 'primary' | 'purple' | 'yellow'>; name: IconName }
  >(plan)
    .with('household', () => ({ color: 'pink', name: 'IconUsers' }))
    .with('householdPlus', () => ({ color: 'pink', name: 'IconUsers' }))
    .with('core', () => ({
      color: 'purple',
      name: 'IconUser',
    }))
    .with('vip', () => ({ color: 'yellow', name: 'IconUserStar' }))
    .with('starter', () => ({ color: 'primary', name: 'IconSeeding' }))
    .with('essentials', () => ({ color: 'primary', name: 'IconSeeding' }))
    .with('champagneHousehold', () => ({ color: 'yellow', name: 'DuckbillLogo' }))
    .with('champagneIndividual', () => ({ color: 'yellow', name: 'DuckbillLogo' }))
    .with('aLaCarte', () => ({ color: 'primary', name: 'IconSeeding' }))
    .with('trial', () => ({ color: 'primary', name: 'IconSeeding' }))
    .exhaustive();

  const planData = planLookup[plan];

  if (isLoading) {
    return (
      <Card
        className={concatClasses('px-xl py-md border-[3px] rounded-lg border-transparent', className)}
        color="primary"
      >
        <XStack gapX="md" justifyContent="between">
          <XStack alignItems="center" gapX="md">
            <YStack bg="lineGray" height={24} radius="full" width={24} />
            <YStack gapY="xs">
              <YStack bg="lineGray" height={18} radius="md" width={100} />
              <YStack bg="lineGray" height={16} radius="md" width={80} />
            </YStack>
          </XStack>
          <YStack bg="lineGray" height={24} radius="md" width={24} />
        </XStack>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className={concatClasses('px-xl py-md border-[3px] rounded-lg border-red-200', className)} color="primary">
        <XStack alignItems="center" gapX="md" justifyContent="between">
          <XStack alignItems="center" gapX="sm">
            <Icon className="text-red-500" name="IconAlertCircle" size={20} />
            <Text color="secondary" type="p-sm">
              Your plan took a duck dive.
            </Text>
          </XStack>
          <Button label="Try again" onClick={() => refetch()} variant="secondary" />
        </XStack>
      </Card>
    );
  }

  const pricing = getMembershipPlanDisplayPricing(planData);
  const hasPromo = Boolean(pricing?.hasDiscount);

  const getMonthlyPrice = () => {
    if (!planData) return '';
    return formatCurrencyNatural(pricing?.primaryPrice ?? planData.price);
  };

  const getFreeCreditsText = () => {
    return promoDetails?.freeTasksCap ? `${promoDetails.freeTasksCap} free credits` : '';
  };

  const getPricingText = () => {
    const monthlyPrice = `${getMonthlyPrice()}/month`;
    const freeCreditsText = getFreeCreditsText();

    if (promoDetails?.isCreateSubscriptionOnSignup) {
      // Show monthly price first, then free credits
      return freeCreditsText ? `${monthlyPrice} + ${freeCreditsText}` : monthlyPrice;
    } else {
      // Show free credits first, then monthly price
      return freeCreditsText ? `${freeCreditsText}, then ${monthlyPrice}` : monthlyPrice;
    }
  };

  return (
    <Card
      className={concatClasses(
        'px-xl py-md border-[3px] rounded-lg',
        applyClass(
          isSelected,
          match(color)
            .with('purple', () => safeCss('border-violet-950'))
            .with('pink', () => safeCss('border-pink-950'))
            .with('yellow', () => safeCss('border-yellow-900'))
            .with('primary', () => safeCss('border-blue-950'))
            .exhaustive(),
          'border-transparent',
        ),
        applyClass(color === 'primary', 'bg-blue-200 hover:bg-blue-100'),
        className,
      )}
      color={color}
      onClick={onClick ? () => onClick?.(plan) : undefined}
    >
      <XStack gapX="md" justifyContent="between">
        <XStack alignItems="center" gapX="md">
          <Icon color={color} name={name} />
          <YStack>
            <Text color={color} type="h6">
              {planData?.label}
            </Text>
            {showCurrentUsers && household?.currentUsers ? (
              <Text color={color} type="h6">
                {household.currentUsers} {pluralize('member', household.currentUsers)}
              </Text>
            ) : null}
            {hidePrice ? null : (
              <Text color={color} type="h6">
                {getPricingText()}
              </Text>
            )}
            {hasPromo ? (
              <Text
                className={concatClasses(
                  'line-through',
                  match(color)
                    .with('purple', () => safeCss('text-violet-600'))
                    .with('pink', () => safeCss('text-pink-600'))
                    .with('yellow', () => safeCss('text-yellow-600'))
                    .with('primary', () => safeCss('text-blue-600'))
                    .exhaustive(),
                )}
                type="p-sm"
              >
                {pricing?.secondaryPrice ? formatCurrencyNatural(pricing.secondaryPrice) : ''}/month
              </Text>
            ) : null}
            {showOwner && household?.ownerName && household.ownerId !== currentClientId ? (
              <Text color={color} type="caption">
                {`You are under ${household.ownerName}'s household`}
              </Text>
            ) : null}
          </YStack>
        </XStack>
        <Icon color={isSelected ? color : 'transparent'} name="IconCheck" size="lg" />
      </XStack>
      {hideBenefitDetails ? null : (
        <List className={concatClasses('list-disc text-p-sm ', getTextColorClassName({ color }))}>
          {planData && isHousehold(planData.planCode) ? (
            <List.Item>
              <Text color={color} type="p-sm">
                Share tasks with the other member on your plan
              </Text>
            </List.Item>
          ) : null}
        </List>
      )}
    </Card>
  );
};
